import Cookies from 'js-cookie';
const faceProfileCache = {};
const faceProfilePromises = {};
export class AdvisorDataService {
    // Helper to safely get nested properties
    get(obj, path, defaultValue) {
        if (!obj || typeof path !== 'string')
            return defaultValue;
        const keys = path.split('.');
        let result = obj;
        for (const key of keys) {
            if (result &&
                typeof result === 'object' &&
                key in result) {
                result = result[key];
            }
            else {
                return defaultValue;
            }
        }
        return result === undefined ? defaultValue : result;
    }
    formatPhone(phone) {
        if (phone) {
            phone = phone.replace(/[() ./-]/g, '');
            phone = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
        }
        return phone;
    }
    normalizeSpaces(str) {
        return str.replace(/ {2,}/g, ' ');
    }
    clean(data) {
        const obj = data ?? {};
        const get = this.get.bind(this);
        const type = get(obj, 'facePrflTyp') === 'Individual' ? 'advisor' : 'team';
        const isAdvisor = type === 'advisor';
        const images = {};
        const urls = {};
        let social = {};
        const designations = isAdvisor
            ? (() => {
                const arr = get(obj, 'facePrflDistributor.facePrflDstrDesignations', []);
                return Array.isArray(arr)
                    ? arr
                        .map((item) => item.desgAcronym)
                        .filter(Boolean)
                        .join(', ') || undefined
                    : undefined;
            })()
            : undefined;
        const distributor = get(obj, 'facePrflDistributor', {});
        const firstNm = typeof distributor?.firstNm === 'string' ? distributor.firstNm : undefined;
        const lastNm = typeof distributor?.lastNm === 'string' ? distributor.lastNm : undefined;
        const midNm = typeof distributor?.midNm === 'string' ? distributor.midNm : undefined;
        // Display name
        const name = isAdvisor
            ? this.normalizeSpaces(`${firstNm ?? ''} ${midNm ?? ''} ${lastNm ?? ''}`)
            : get(obj, 'prflPractice.pracNm') ??
                get(obj, 'prflNkNm') ??
                '';
        const title = isAdvisor && typeof distributor === 'object' && distributor !== null
            ? distributor.profTtl
            : undefined;
        const imagesArr = get(obj, 'prflImages', []);
        if (Array.isArray(imagesArr)) {
            imagesArr.forEach((image) => {
                if (image &&
                    typeof image.photoSizeCd === 'string' &&
                    image.photoImgURL) {
                    const sizeCode = image.photoSizeCd.replace(' image', '').toLowerCase();
                    images[sizeCode] = image.photoImgURL;
                }
            });
        }
        const socialArr = Array.isArray(get(obj, 'prflSocialMediaURLs', []))
            ? get(obj, 'prflSocialMediaURLs', [])
            : [];
        if (Array.isArray(socialArr)) {
            socialArr.forEach((item) => {
                if (!item || typeof item !== 'object')
                    return;
                const { mediaTypCd, URL } = (item ?? {});
                switch (mediaTypCd) {
                    case 'siteurl':
                    case 'TeamWebsiteUrl':
                        urls.site = URL;
                        break;
                    case 'referme':
                        urls.referme = URL;
                        break;
                    case 'FacebooklinkUrl':
                    case 'TeamFacebooklinkUrl':
                        social.facebook = URL;
                        break;
                    case 'linkedinurl':
                        social.linkedin = URL;
                        break;
                    case 'BiopageUrl':
                        urls.site = URL;
                        break;
                }
            });
        }
        // Patch: If both referme and site are missing, try to infer from known mock structure
        const prflWebsite = typeof obj.prflWebsite === 'object' && obj.prflWebsite !== null
            ? obj.prflWebsite
            : undefined;
        // Remove redeclaration and useless assignment for prflReferme, use only once below
        let prflReferme = undefined;
        if (typeof obj.prflReferme === 'object' && obj.prflReferme !== null) {
            prflReferme = obj.prflReferme;
        }
        if (!urls.site &&
            prflWebsite &&
            typeof prflWebsite === 'object' &&
            typeof prflWebsite['URL'] === 'string') {
            urls.site = prflWebsite['URL'];
        }
        if (!urls.referme &&
            prflReferme &&
            typeof prflReferme === 'object' &&
            typeof prflReferme['URL'] === 'string') {
            urls.referme = prflReferme['URL'];
        }
        // Parse prflWebsite and prflReferme for URLs (support both { URL } and direct string properties)
        if (!urls.site && prflWebsite && typeof prflWebsite === 'object') {
            if (typeof prflWebsite['URL'] === 'string' &&
                prflWebsite['URL'].startsWith('http')) {
                urls.site = prflWebsite['URL'];
            }
            else {
                for (const key in prflWebsite) {
                    const val = prflWebsite[key];
                    if (typeof val === 'string' && val.startsWith('http')) {
                        urls.site = val;
                        break;
                    }
                }
            }
        }
        if (!urls.referme && prflReferme && typeof prflReferme === 'object') {
            if (typeof prflReferme['URL'] === 'string' &&
                prflReferme['URL'].includes('referral')) {
                urls.referme = prflReferme['URL'];
            }
            else {
                for (const key in prflReferme) {
                    const val = prflReferme[key];
                    if (typeof val === 'string' && val.includes('referral')) {
                        urls.referme = val;
                        break;
                    }
                }
            }
        }
        // Always ensure social is a plain object, even if all values are undefined or input is malformed
        if (!social || typeof social !== 'object' || Array.isArray(social)) {
            social = {};
        }
        return {
            designations,
            email: get(obj, 'prflEmail.emailAddr'),
            id: get(obj, 'facePrflDistributor.facePrflDstrId'),
            image: images.large || images.medium || images.small,
            name: name ?? '',
            phone: this.formatPhone(get(obj, 'prflTelephone.teleNbr')),
            title,
            type,
            urls,
            social
        };
    }
    /**
     * Get a face profile by id
     */
    async getById(id, skipCache = false) {
        if (!id) {
            return {
                data: null,
                errors: []
            };
        }
        const cacheKey = id;
        if (!skipCache && faceProfileCache[cacheKey]) {
            return faceProfileCache[cacheKey];
        }
        if (faceProfilePromises[cacheKey] !== undefined) {
            return faceProfilePromises[cacheKey];
        }
        const url = `/api/faceProfile?${new URLSearchParams({
            facePrflId: id
        }).toString()}`;
        const fetchPromise = fetch(url, {
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'OData-Version': '4.0'
            }
        })
            .then(async (res) => {
            if (!res.ok) {
                const error = new Error(`HTTP error: ${res.status}`);
                error.status = res.status;
                throw error;
            }
            return res.json();
        })
            .then(res => {
            if (!res || typeof res !== 'object') {
                return { data: null, errors: [new Error('Malformed response')] };
            }
            return { data: this.clean(res), errors: [] };
        })
            .catch(err => {
            return {
                data: null,
                errors: [
                    err instanceof Error
                        ? err
                        : new Error(String(err ?? 'Unknown error'))
                ]
            };
        });
        faceProfilePromises[cacheKey] = fetchPromise;
        fetchPromise.then(result => {
            faceProfileCache[cacheKey] = result;
            delete faceProfilePromises[cacheKey];
        });
        return fetchPromise;
    }
    /**
     * Get a face profile using the FaceProfileCookie (id stored in cookie)
     */
    async getByCookie() {
        try {
            const faceProfileId = Cookies.get('FaceProfileCookie');
            if (!faceProfileId || faceProfileId.length === 0) {
                return {
                    data: null,
                    errors: [new Error('No face profile id found in cookie 1')]
                };
            }
            return this.getById(faceProfileId);
        }
        catch (err) {
            return {
                data: null,
                errors: [err instanceof Error ? err : new Error(String(err))]
            };
        }
    }
    static clearCache() {
        Object.keys(faceProfileCache).forEach(key => delete faceProfileCache[key]);
        Object.keys(faceProfilePromises).forEach(key => delete faceProfilePromises[key]);
    }
}
